import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IgntrIconComponent } from 'app/shared/igntr';
import { DashboardCardComponent } from './dashboard-card.component';

export interface LearningProgress {
  title: string;
  totalSteps: number;
  currentStep: number;
  currentStepTitle: string;
  resumeLink?: string;
  percentComplete?: number;
}

@Component({
  selector: 'app-dashboard-continue-learning',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoRootModule,
    IgntrIconComponent,
    DashboardCardComponent,
  ],
  template: `
    <app-dashboard-card
      [title]="'dashLearningTitle' | transloco"
      [sub]="subText"
    >
      <a
        card-action
        routerLink="/sprints"
        class="text-[14px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
      >
        {{ 'dashLearningSeeAll' | transloco }}
        <igntr-icon name="arrowR" [size]="14"></igntr-icon>
      </a>

      @if (!progress) {
        <div
          class="rounded-xl bg-gradient-to-br from-slate-50/50 to-white p-4 text-center text-[14px] text-slate-400"
          style="border: 1px solid rgba(226,232,240,0.7);"
        >
          {{ 'dashLearningEmpty' | transloco }}
        </div>
      } @else {
        <div
          class="rounded-xl bg-gradient-to-br from-slate-50/50 to-white p-4"
          style="border: 1px solid rgba(226,232,240,0.7);"
        >
          <div class="flex items-center gap-3 flex-wrap">
            <div
              class="steps-track flex items-center gap-1.5 w-full min-w-0 overflow-x-auto"
            >
              @for (i of stepIndices; track i) {
                <div
                  [class]="
                    'w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[14px] font-mono font-semibold ' +
                    (i < progress.currentStep
                      ? 'bg-emerald-500 text-white'
                      : i === progress.currentStep
                        ? 'bg-accent text-white'
                        : 'bg-slate-200 text-slate-500')
                  "
                >
                  @if (i < progress.currentStep) {
                    <igntr-icon name="check" [size]="12"></igntr-icon>
                  } @else {
                    {{ i + 1 }}
                  }
                </div>
              }
            </div>

            <div class="flex-1 min-w-0">
              <div
                class="text-[14px] uppercase tracking-[0.12em] text-slate-400 font-medium"
              >
                {{
                  'dashLearningStep'
                    | transloco
                      : {
                          current: progress.currentStep + 1,
                          total: progress.totalSteps,
                        }
                }}
              </div>
              <div
                class="text-[14px] font-semibold text-slate-900 mt-0.5 truncate"
              >
                {{ progress.currentStepTitle }}
              </div>
              @if (progress.title) {
                <div class="text-[14px] text-slate-500 mt-0.5 truncate">
                  {{ progress.title }}
                </div>
              }
            </div>

            <a
              [routerLink]="progress.resumeLink || '/sprints'"
              class="px-4 py-2 rounded-lg bg-accent text-white text-[14px] font-medium hover:opacity-95 inline-flex items-center gap-1.5"
            >
              {{ 'dashLearningResume' | transloco }}
              <igntr-icon name="play" [size]="14"></igntr-icon>
            </a>
          </div>
        </div>
      }
    </app-dashboard-card>
  `,
  styles: [
    `
      .steps-track {
        scrollbar-width: thin;
        scrollbar-color: #cbd5e1 transparent;
        padding-bottom: 2px;
      }

      .steps-track::-webkit-scrollbar {
        height: 6px;
      }

      .steps-track::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 999px;
      }

      .steps-track::-webkit-scrollbar-track {
        background: transparent;
      }
    `,
  ],
})
export class DashboardContinueLearningComponent {
  @Input() progress: LearningProgress | null = null;

  get stepIndices(): number[] {
    if (!this.progress) return [];
    return Array.from({ length: this.progress.totalSteps }, (_, i) => i);
  }

  get subText(): string {
    if (!this.progress) return '';
    return this.progress.title;
  }
}
